import React, { FC, useState, memo } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import { SkladonMappingType } from '../../types/skladonMapping';
import Typography from 'src/components/Typography';
import Grid, { GridItem } from 'src/components/Grid';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { styled } from '@mui/material/styles';
import { colors } from 'src/themes/default';
import translate from '@translate';
import {
    integrationProductTypeOptions,
    integrationRetinoKeyOptions
} from './integrationMappingDisplayOptions';

export type OthersEditKeysType = 'UID' | 'VALUE' | 'NAME' | 'IMPORT' | 'EXPORT' | 'KEY';

// Table columns for ORDER_TYPE
const orderTypeColumns: TableColumnType[] = [
    {
        key: 'UID',
        label: 'uid',
        customRender: true
    },
    {
        key: 'NAME',
        label: 'name'
    },
    {
        key: 'IMPORT',
        label: 'import',
        type: 'select',
        selectOption: 'boolNumberCodes',
        customRender: true
    },
    {
        key: 'EXPORT',
        label: 'export',
        type: 'select',
        selectOption: 'boolNumberCodes',
        customRender: true
    },
    {
        key: 'actions',
        label: 'actions',
        customRender: true
    }
];

// Table columns for RETINO
const retinoColumns: TableColumnType[] = [
    {
        key: 'KEY',
        label: 'key',
        type: 'select',
        selectOption: 'retinoKeyCodes',
        customRender: true
    },
    {
        key: 'UID',
        label: 'uid',
        customRender: true
    },
    {
        key: 'VALUE',
        label: 'value',
        customRender: true
    },
    {
        key: 'NAME',
        label: 'name',
        customRender: true
    },
    {
        key: 'actions',
        label: 'actions',
        customRender: true
    }
];

const ProductTypeBlock = styled('div')({
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: colors.background_W
});

const OrderTypeBlock = styled('div')({
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: colors.background_W
});

const RetinoBlock = styled('div')({
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: colors.background_W
});

const AddButton = styled('div')({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: colors.primary_O,
    marginTop: '10px',
    '& svg': {
        marginRight: '5px'
    }
});

export interface OthersTablePropsType {
    productTypeData?: SkladonMappingType | null;
    orderTypeData?: SkladonMappingType[] | null;
    retinoData?: SkladonMappingType[] | null;
    boolNumberCodes: ConstantsCodeType[];
    changeProductTypeValue: (value: string) => void;
    changeOrderTypeValue: (key: OthersEditKeysType, value: any, error: boolean, id: number) => void;
    changeRetinoValue?: (key: OthersEditKeysType, value: any, error: boolean, id: number) => void;
    addOrderType: () => void;
    removeOrderType: (id: number) => void;
    addRetinoMapping?: () => void;
    removeRetinoMapping?: (id: number) => void;
    loading: boolean;
    hasRetinoConfig?: boolean;
    providerSystem?: string;
}

const OthersTable: FC<OthersTablePropsType> = (props) => {
    const getInputName = (id: number, key: string) => `product-${id}-${key}`;

    // Handle product type selection
    const handleProductTypeChange = (value: any, error: boolean) => {
        props.changeProductTypeValue(String(value));
    };

    // Custom render for ORDER_TYPE table
    const orderTypeCustomRender: CustomRenderType = (column, row) => {
        const rowId = row.ID || 0;
        const name = getInputName(rowId, column.key);
        switch (column.key) {
            case 'UID':
                return (
                    <TextFieldDebounced
                        value={row.UID || ''}
                        onChange={(value, error) => props.changeOrderTypeValue('UID', value, error, rowId)}
                        name={name}
                        required={true}
                        fullWidth
                    />
                );
            case 'IMPORT':
                return (
                    <Select
                        options={props.boolNumberCodes}
                        value={row.IMPORT || '1'}
                        onChange={(value, error) => props.changeOrderTypeValue('IMPORT', value, error, rowId)}
                        name={name}
                        fullWidth
                    />
                );
            case 'EXPORT':
                return (
                    <Select
                        options={props.boolNumberCodes}
                        value={row.EXPORT || '1'}
                        onChange={(value, error) => props.changeOrderTypeValue('EXPORT', value, error, rowId)}
                        name={name}
                        fullWidth
                    />
                );
            case 'actions':
                return (
                    <HighlightOffIcon
                        style={{ color: colors.text_T0, cursor: 'pointer' }}
                        onClick={() => props.removeOrderType(rowId)}
                    />
                );
            default:
                return null;
        }
    };

    // Custom render for RETINO table
    const retinoCustomRender: CustomRenderType = (column, row) => {
        const rowId = row.ID || 0;
        const name = getInputName(rowId, column.key);
        switch (column.key) {
            case 'KEY':
                return (
                    <Select
                        options={integrationRetinoKeyOptions}
                        value={row.KEY || ''}
                        onChange={(value, error) => props.changeRetinoValue && props.changeRetinoValue('KEY', value, error, rowId)}
                        name={name}
                        fullWidth
                    />
                );
            case 'UID':
                return (
                    <TextFieldDebounced
                        value={row.UID || ''}
                        onChange={(value, error) => props.changeRetinoValue && props.changeRetinoValue('UID', value, error, rowId)}
                        name={name}
                        fullWidth
                    />
                );
            case 'VALUE':
                return (
                    <TextFieldDebounced
                        value={row.VALUE || ''}
                        onChange={(value, error) => props.changeRetinoValue && props.changeRetinoValue('VALUE', value, error, rowId)}
                        name={name}
                        fullWidth
                    />
                );
            case 'NAME':
                return (
                    <TextFieldDebounced
                        value={row.NAME || ''}
                        onChange={(value, error) => props.changeRetinoValue && props.changeRetinoValue('NAME', value, error, rowId)}
                        name={name}
                        fullWidth
                    />
                );
            case 'actions':
                return (
                    <HighlightOffIcon
                        style={{ color: colors.text_T0, cursor: 'pointer' }}
                        onClick={() => props.removeRetinoMapping && props.removeRetinoMapping(rowId)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            {/* PRODUCT_TYPE section */}
            <ProductTypeBlock>
                <Typography variant="h3" gutterBottom>
                    {translate('integrationDetail.productType')}
                </Typography>
                <Typography variant="body2" gutterBottom>
                    {translate('integrationDetail.productTypeDescription')}
                </Typography>
                <Grid spacing={2}>
                    <GridItem cols={12} >
                        <Select
                            options={integrationProductTypeOptions}
                            value={props.productTypeData?.UID || 'czechFifoProductDefaults'}
                            onChange={handleProductTypeChange}
                            name="product-type-select"
                            fullWidth
                            label={translate('integrationDetail.productTypeLabel')}
                        />
                    </GridItem>
                </Grid>
            </ProductTypeBlock>

            {/* ORDER_TYPE section - zobrazit pouze pokud je SYSTEM u providera rovno SHOPTET */}
            {props.providerSystem === 'SHOPTET' && (
                <OrderTypeBlock>
                    <Typography variant="h3" gutterBottom>
                        {translate('integrationDetail.orderType')}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        {translate('integrationDetail.orderTypeDescription')}
                    </Typography>

                    <TableSimple
                        rowIdKey="ID"
                        rows={props.orderTypeData || []}
                        columsLabelPrefix="integration.othersTable"
                        allColumns={orderTypeColumns}
                        initSort={{ column: 'UID', direction: 'ASC', createIdNumber: true }}
                        selectOptions={{
                            boolNumberCodes: props.boolNumberCodes
                        }}
                        customRender={orderTypeCustomRender}
                        disableFilter
                        maxHeight={'unset'}
                        defaultItems={100}
                        loading={props.loading}
                    />

                    <AddButton onClick={props.addOrderType}>
                        <AddCircleIcon />
                        <Typography variant="body1">
                            {translate('integrationDetail.addOrderType')}
                        </Typography>
                    </AddButton>
                </OrderTypeBlock>
            )}

            {/* RETINO section - zobrazí se pouze pokud má provider konfiguraci pro Retino */}
            {props.hasRetinoConfig && (
                <RetinoBlock>
                    <Typography variant="h3" gutterBottom>
                        {translate('integrationDetail.retino')}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        {translate('integrationDetail.retinoDescription')}
                    </Typography>

                    <TableSimple
                        rowIdKey="ID"
                        rows={props.retinoData || []}
                        columsLabelPrefix="integration.retinoTable"
                        allColumns={retinoColumns}
                        initSort={{ column: 'KEY', direction: 'ASC', createIdNumber: true }}
                        selectOptions={{
                            retinoKeyCodes: integrationRetinoKeyOptions
                        }}
                        customRender={retinoCustomRender}
                        disableFilter
                        maxHeight={'unset'}
                        loading={props.loading}
                        defaultItems={100}
                    />

                    <AddButton onClick={() => props.addRetinoMapping && props.addRetinoMapping()}>
                        <AddCircleIcon />
                        <Typography variant="body1">
                            {translate('integrationDetail.addRetinoMapping')}
                        </Typography>
                    </AddButton>
                </RetinoBlock>
            )}
        </>
    );
};

export default memo(OthersTable);
